import { FunctionComponent } from "react"
import { useRouter } from "next/router"
import Head from "next/head"

import getSettings from "@/services/settings"
import getCars from "@/services/cars"
import { ICars } from "@/types/contentful"
import CarsPage from "@/components/CarsPage/CarsPage"
import FormStep from "@/components/FormSteps/FormSteps"
import Section from "@/components/Section/Section"
import Direction from "@/components/Direction/Direction"
import { getDistance } from "@/services/map"
import { Settings } from "@/types/settings"

import styles from "./styles.module.scss"

interface CarsProps {
  cars: ICars[]
  distance?: number
}

const Cars: FunctionComponent<CarsProps> = ({ cars, distance }) => {
  const router = useRouter()

  const { from_place_id, to_place_id, time, hours, d, from, to } = router.query

  const isLocationStep = from_place_id && to_place_id && time
  const isHoursStep = from_place_id && hours && time

  return (
    <>
      <Head>
        <title>Our cars</title>
        <meta
          name="description"
          content="Luxury chauffeur cars available for hire, from Mercedes. The definitive collection of private London cars."
        />
        <meta name="robot" content="noindex, nofollow" />
      </Head>
      <Section sectionClassName={styles.section}>
        {d && from && to && (
          <Direction date={String(d)} from={String(from)} to={String(to)} />
        )}
        {(isLocationStep || isHoursStep) && <FormStep currentStep={2} />}
        <CarsPage cars={cars} hours={hours as string} distance={distance} />
      </Section>
    </>
  )
}

export default Cars

export async function getServerSideProps(context: any) {
  if (Object.keys(context.query).length === 0) {
    return {
      redirect: {
        destination: "/executive-car-service",
        permanent: true,
      },
    }
  }

  const { from_place_id, to_place_id, time } = context.query
  const settingsPromise = getSettings()
  const carsPromise = getCars({ include: 2 })
  const promises: Promise<Settings | ICars[] | ICars | number | null>[] = [
    settingsPromise,
    carsPromise,
  ]

  if (from_place_id && to_place_id) {
    promises.push(
      getDistance({
        from_place_id,
        to_place_id,
        departureTime: Number(time),
      })
    )
  }

  const [settings, cars, distance] = await Promise.all(promises).catch((e) => {
    // TODO handle error
    return []
  })

  if (!settings) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    }
  }

  const newCars = (cars as ICars[]).filter((car) => car.fields.mainCar === true)

  newCars.sort(
    (first: ICars, second: ICars) =>
      first.fields.priority - second.fields.priority
  )

  return {
    props: { settings, cars: newCars, distance: distance || null },
  }
}
